/** Attaching files to a message: the button, and what is attached - an image as a
 * thumbnail, a document by its name.
 * Two pieces so a composer can put the button above Send and the thumbnails under the
 * box, while the "Start a conversation" card keeps them on one row. */
import { useRef } from "react";

import { MAX_ATTACHMENTS, type Attachments } from "../api/hooks/useAttachments";
import styles from "./AttachmentsBar.module.css";

export const AttachButton = ({
  attachments,
  disabled,
}: {
  attachments: Attachments;
  disabled: boolean;
}) => {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          attachments.add(Array.from(event.target.files ?? []));
          // So picking the same file again after removing it still fires.
          event.target.value = "";
        }}
      />
      <button
        type="button"
        className={styles.attach}
        disabled={disabled || attachments.items.length >= MAX_ATTACHMENTS}
        aria-label="Attach files"
        title="Attach documents or images, or paste one into the box. A document is read for this conversation only, unless the agent saves it to the project."
        onClick={() => input.current?.click()}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path
            d="M13.5 7.6 8 13.1a3.6 3.6 0 0 1-5.1-5.1l5.7-5.7a2.4 2.4 0 0 1 3.4 3.4L6.3 11.4a1.2 1.2 0 0 1-1.7-1.7l5.2-5.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
};

/** Renders nothing until something is attached or refused, so it costs no space. */
export const AttachedFiles = ({
  attachments,
  disabled,
}: {
  attachments: Attachments;
  disabled: boolean;
}) => {
  const failed = attachments.items.filter((item) => item.status === "failed");
  if (!attachments.items.length && !attachments.rejection) return null;
  return (
    <>
      {attachments.items.map((item) => (
        <figure
          key={item.id}
          className={`${styles.thumb} ${item.kind === "document" ? styles.doc : ""} ${
            item.status === "failed" ? styles.failed : ""
          }`}
          title={item.error ?? item.name}
        >
          {item.previewUrl ? (
            <img src={item.previewUrl} alt={item.name} />
          ) : (
            <span className={styles.noPreview}>
              {item.kind === "document" && <span className={styles.kind}>Document</span>}
              {item.name}
            </span>
          )}
          {item.status === "uploading" && <span className={styles.state}>Uploading…</span>}
          <button
            type="button"
            className={styles.remove}
            aria-label={`Remove ${item.name}`}
            disabled={disabled}
            onClick={() => attachments.remove(item.id)}
          >
            ×
          </button>
        </figure>
      ))}
      {(attachments.rejection || failed.length > 0) && (
        <p className={styles.error}>
          {[
            attachments.rejection,
            ...failed.map((item) => `${item.name}: ${item.error ?? "upload failed"}`),
          ]
            .filter(Boolean)
            .join("; ")}
        </p>
      )}
    </>
  );
};
